'use client';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    History
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, type HistoryWatchLiveRoom } from '@/lib/api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipArrow } from '@/components/ui/tooltip';
import { UserAvatar } from '@/app/components/Lives/UserBase/avatar';
import { formatNumber } from '@/app/utils/useFormat';
import { UserAttend } from '@/app/components/Lives/UserAttend'
import { Loading } from '@/components/loading';
import { Empty } from '@/components/empty';
import dayjs from 'dayjs';

export default function CoWallet() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate();
    const [groupedHistoryData, setGroupedHistoryData] = useState<{
        today: HistoryWatchLiveRoom[];
        earlier: HistoryWatchLiveRoom[];
    }>({ today: [], earlier: [] })

    // 按日期分组数据的函数
    const groupDataByDate = useCallback((data: HistoryWatchLiveRoom[]) => {;
        const todayStart = dayjs().startOf('day');
        const todayList: HistoryWatchLiveRoom[] = [];
        const earlierList: HistoryWatchLiveRoom[] = [];

        data.forEach(item => {
            const itemDate = dayjs(item.watch_time)
            if (itemDate.isAfter(todayStart)) {
                todayList.push(item);
            } else {
                earlierList.push(item);
            }
        });

        return {
            today: todayList,
            earlier: earlierList
        };
    }, []);

    // 渲染list
    const renderItem = useCallback((item: HistoryWatchLiveRoom, index: number, type: 'today' | 'earlier') => {
        return (
            <div
                className='flex items-center gap-2 justify-between'
                key={`today-${index}`}
            >
                <div className='flex items-center gap-2'>
                    <div className='scale-75'>
                        <UserAvatar
                            userInfo={{
                                userId: item.streamer_id,
                                userName: item.streamer_user_name,
                                avatar: item.streamer_current_avatar,
                                status: item.streamer_is_room,
                                followStatus: item.relationship_status,
                                followCount: item.fans_count,
                                totalViewCount: 0,
                                compareStatus: 0,
                                updateTime: item.created_at,
                                createTime: item.streamer_create_time,
                                roomId: item.streamer_room_id ||item.room_id
                            }}
                            hideLiveStatus={false}
                        />
                    </div>
                    <div className='flex flex-col gap-1'>
                        <span
                            className="text-ellipsis overflow-hidden whitespace-nowrap text-sm text-font-primary cursor-pointer hover:text-font-primary/80 hover:underline"
                            style={{ maxWidth: `120px` }}
                            onClick={() => {
                               navigate(`/${item?.streamer_handle}`);
                            }}
                        >
                            {item.streamer_user_name}
                        </span>
                        <div className='flex items-center gap-1 text-xs'>
                            <div className="flex items-left gap-1">
                                <span className="font-medium text-font-primary opacity-50">{t('header.following')}</span>
                                <h3 className="font-medium line-clamp-1 text-font-primary break-all">
                                    {formatNumber(item.follow_count)}
                                </h3>
                            </div>
                            <div className="flex items-left gap-1">
                                <span className="font-medium text-font-primary opacity-50">{t('header.followers')}</span>
                                <h3 className="font-medium line-clamp-1 text-font-primary break-all">
                                    {formatNumber(item.fans_count)}
                                </h3>
                            </div>
                        </div>
                    </div>
                </div>
                <UserAttend
                    status={item.relationship_status}
                    userId={item.streamer_id}
                    className='h-6 text-xxs px-2 mr-2 w-auto'
                    confirmDisabled={true}
                    onChangeFollow={() => {
                        // 重置关注状态
                        setGroupedHistoryData((prev) => {
                            const newUser = [...prev[type]]
                            newUser[index].relationship_status = newUser[index].relationship_status ? 0 : 1
                            return {
                                ...prev,
                                [type]: newUser
                            }
                        })
                    }}
                />
            </div>
        )
    }, [])

    // 获取数据
    const fetchData = useCallback(async () => {
        setLoading(true)
        try {
            const response = await api.room.getMyWatchHistory({
                page: 1,
                limit: 30
            })
            const { data } = response;
            const list = data?.list || [];
            // 按日期分组数据
            const groupedData = groupDataByDate(list);
            setGroupedHistoryData(groupedData);
        } catch (err) {
            console.error('获取首页数据失败:', err)
        } finally {
            setLoading(false)
        }
    }, [groupDataByDate])

    return (
        <DropdownMenu onOpenChange={e => {
            if (e) {
                fetchData()
            }
            else {
                setGroupedHistoryData({ today: [], earlier: [] })
            }
        }}>
            <DropdownMenuTrigger asChild>
                <div
                    className='max-md:hidden transition-all flex items-center cursor-pointer gap-1 rounded-md bg-background-card border border-button-border/85 relative p-2 hover:border-button-border text-font-secondary hover:text-font-primary hover:bg-secondary-hover'
                >
                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <History className='w-4 h-4' />
                            </TooltipTrigger>
                            <TooltipContent className="TooltipContent" sideOffset={10} arrowPadding={10}>
                                {t('header.historyRecord')}
                                <TooltipArrow className="TooltipArrow" />
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' sideOffset={10} className='px-4 pt-2 pb-4 flex flex-col max-h-[300px] overflow-y-auto w-[384px]'>
                {
                    loading ? (
                        <Loading />
                    ) : (groupedHistoryData.today.length === 0 && groupedHistoryData.earlier.length === 0) ? (
                        <Empty>
                            {t('empty.noData')}
                        </Empty>
                    ) : (
                        <>
                            {/* 今天的数据 */}
                            {groupedHistoryData.today.length > 0 && (
                                <div className='flex flex-col gap-3 relative pb-2 -mr-3'>
                                    <div className='flex items-center gap-1'>
                                        <div className="w-1.5 h-1.5 bg-white rounded-full mr-1"></div>
                                        <span className='text-sm text-font-primary'>{t('header.today')}</span>
                                    </div>
                                    <div className='flex flex-col pl-6 gap-2'>
                                        {groupedHistoryData.today.map((item, index) => renderItem(item, index, 'today'))}
                                    </div>
                                    <div className='w-[0.5px] absolute left-0.5 top-1.5 bottom-0 bg-button-border'></div>
                                </div>
                            )}

                            {/* 更早的数据 */}
                            {groupedHistoryData.earlier.length > 0 && (
                                <div className='flex flex-col gap-3 relative -mr-3'>
                                    <div className='flex items-center gap-1'>
                                        <div className="w-1.5 h-1.5 bg-white rounded-full mr-1"></div>
                                        <span className='text-sm text-font-primary'>{t('header.earlier')}</span>
                                    </div>
                                    <div className='flex flex-col pl-6 gap-2'>
                                        {groupedHistoryData.earlier.map((item, index) => renderItem(item, index, 'earlier'))}
                                    </div>
                                    <div className='w-[0.5px] absolute left-0.5 top-5 bottom-0 bg-button-border'></div>
                                </div>
                            )}
                        </>
                    )
                }
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
